import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { Check, CheckCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode, hostname } from "../utils";
import { useNavigate } from "react-router-dom";
import {
  SubscriptionTierCard,
  SubscriptionTierCardSkeleton,
  TierData,
} from "../components/SubscriptionTierCard";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface PricingProps {
  mode: string;
}

// Feature key to display name mapping
const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  dashboard: "Dashboard & Analytics",
  chat: "AI Chat",
  inbox: "Inboxes",
  "content-engine": "Content Engine",
  "smart-list": "Smart Lists",
  sequence: "Sequences",
  image: "AI Image Generation",
  audio: "AI Audio Generation",
  video: "AI Video Generation",
  workflow: "Workflows",
  collaborators: "Team Collaborators",
  integrations: "Integrations (CRM, Slack, etc.)",
  employee: "AI Employees",
  "knowledge-base": "Knowledge Base",
  agent: "AI Agents",
  api: "API Access",
  support: "Priority Support",
  whitelabel: "White-Label Instance",
  sso: "Single Sign-On (SSO)",
};

// Order in which features should appear in the comparison table
const FEATURE_ORDER = [
  "dashboard",
  "chat",
  "employee",
  "knowledge-base",
  "workflow",
  "smart-list",
  "sequence",
  "inbox",
  "agent",
  "content-engine",
  "image",
  "audio",
  "video",
  "collaborators",
  "integrations",
  "whitelabel",
  "api",
  "support",
  "sso",
];

const processFeaturePlaceholders = (feature: string, tier: TierData): string => {
  const companies = tier.baseCompanies || 0;
  const seats = tier.baseSeats || 1;
  const totalCredits = tier.baseCreditLimit || tier.creditLimit || 0;

  const replacements: Record<string, string> = {
    companies: companies > 0 ? companies.toString() : "Custom",
    companiesLabel: companies === 1 ? "company" : "companies",
    seats: seats > 0 ? seats.toString() : "Custom",
    seatsLabel: seats === 1 ? "seat" : "seats",
    totalCredits: totalCredits > 0 ? totalCredits.toLocaleString() : "Custom",
  };

  return feature
    .replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : ""
    )
    .replace(/\s+/g, " ")
    .trim();
};

const Pricing: React.FC<PricingProps> = ({ mode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { whitelabelSettings, whitelabelLogos, loaded } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Redirect if whitelabel has custom subscription handling
  useEffect(() => {
    if (loaded && whitelabelSettings?.customSubscriptionEnabled) {
      if (whitelabelSettings.pricingUrl) {
        window.location.href = whitelabelSettings.pricingUrl;
      } else {
        navigate("/login", { replace: true });
      }
    }
  }, [loaded, whitelabelSettings, navigate]);

  // Don't render while loading or if redirect is pending
  if (!loaded || whitelabelSettings?.customSubscriptionEnabled) {
    return null;
  }

  // Responsive breakpoints for tier table chunking
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 1 per row
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // 2 per row
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg")); // 3 per row
  // lg and up = 4 per row

  // Get number of tiers per row based on screen size
  const getTiersPerRow = () => {
    if (isXs) return 1;
    if (isSm) return 2;
    if (isMd) return 3;
    return 4;
  };

  const [tiers, setTiers] = useState<TierData[]>([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<"annual" | "monthly">(
    "annual"
  );

  // Get logo URL with dark mode support
  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";

  // Load tier data from backend
  useEffect(() => {
    const loadTierData = async () => {
      try {
        setTiersLoading(true);
        const currentDomain = window.location.hostname;
        const response = await axios.get(`${hostname}/subscriptions/tiers`, {
          params: { domain: currentDomain },
        });

        if (response.data?.tiers) {
          setTiers(response.data.tiers);
        }
      } catch (error) {
        console.error("Error loading tier data:", error);
      } finally {
        setTiersLoading(false);
      }
    };

    loadTierData();
  }, []);

  const handlePlanClick = (tier: TierData) => {
    if (tier.isContactSales && tier.salesEmail) {
      window.open(
        `mailto:${tier.salesEmail}?subject=Enterprise Plan Inquiry`,
        "_blank"
      );
    } else {
      navigate("/signup");
    }
  };

  const FeatureCheck = ({ available }: { available: boolean }) => {
    if (available) {
      return <CheckCircleOutline sx={{ color: "primary.main", fontSize: 20 }} />;
    }
    return <RemoveCircleOutline sx={{ color: "text.disabled", fontSize: 20 }} />;
  };

  // Build unique feature list from all tiers' features_status, ordered by FEATURE_ORDER
  // Only include features that are enabled for at least one tier
  const allFeatures = useMemo(() => {
    const featureSet = new Set<string>();

    // Collect all feature keys from all tiers
    tiers.forEach((tier) => {
      if (tier.features_status) {
        Object.keys(tier.features_status).forEach((key) => {
          featureSet.add(key);
        });
      }
    });

    // Filter to only features that are enabled for at least one tier
    const enabledFeatures = Array.from(featureSet).filter((featureKey) => {
      return tiers.some((tier) => tier.features_status?.[featureKey] === "enabled");
    });

    // Sort features according to FEATURE_ORDER, putting unknown features at the end
    const sortedFeatures = enabledFeatures.sort((a, b) => {
      const indexA = FEATURE_ORDER.indexOf(a);
      const indexB = FEATURE_ORDER.indexOf(b);
      // If not in order list, put at end
      const orderA = indexA === -1 ? 999 : indexA;
      const orderB = indexB === -1 ? 999 : indexB;
      return orderA - orderB;
    });

    return sortedFeatures;
  }, [tiers]);

  // Check if a tier has a specific feature enabled
  const tierHasFeature = (tier: TierData, featureKey: string): boolean => {
    if (!tier.features_status) return false;
    const status = tier.features_status[featureKey];
    return status === "enabled";
  };

  // Get display name for a feature key
  const getFeatureDisplayName = (featureKey: string): string => {
    return FEATURE_DISPLAY_NAMES[featureKey] || featureKey;
  };

  // Calculate grid columns based on number of tiers
  const getGridColumns = () => {
    const tierCount = tiers.length;
    if (tierCount === 1) return 12;
    if (tierCount === 2) return 6;
    if (tierCount === 3) return 4;
    return 3;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Pricing
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Choose the plan that fits your needs.
          </Typography>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        {/* Billing Period Toggle */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <ToggleButtonGroup
            value={billingPeriod}
            exclusive
            onChange={(_, value) => value && setBillingPeriod(value)}
          >
            <ToggleButton value="annual">
              Annual
              <Chip label="Save 20%" size="small" color="primary" sx={{ ml: 1 }} />
            </ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {tiersLoading ? (
          <Grid container spacing={3} justifyContent="center">
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <SubscriptionTierCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : (
          // Render tiers in table layout with responsive chunking
          (() => {
            const tiersPerRow = getTiersPerRow();
            const chunkedTiers: TierData[][] = [];
            for (let i = 0; i < tiers.length; i += tiersPerRow) {
              chunkedTiers.push(tiers.slice(i, i + tiersPerRow));
            }

            // Cell style matching Card variant="outlined" with p: 3
            const cellStyle = {
              border: "none",
              p: 0,
              verticalAlign: "top",
            };

            // Empty cell style for padding incomplete rows
            const emptyCellStyle = {
              border: "none",
              p: 0,
            };

            // Helper to render empty cells for incomplete rows
            const renderEmptyCells = (tierChunk: TierData[], rowIndex: number) => {
              const emptyCount = tiersPerRow - tierChunk.length;
              if (emptyCount <= 0) return null;
              return Array.from({ length: emptyCount }, (_, i) => (
                <TableCell key={`empty-${rowIndex}-${i}`} sx={emptyCellStyle} />
              ));
            };

            return chunkedTiers.map((tierChunk, chunkIndex) => (
              <Box
                key={`tier-table-${chunkIndex}`}
                sx={{
                  mb: chunkIndex < chunkedTiers.length - 1 ? "18px" : 0, // 18px between rows
                }}
              >
                <Table
                  sx={{
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: "18px 0", // Space between columns
                    width: "100%",
                  }}
                >
                  <TableBody>
                    {/* Row 1: Tier Names */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`name-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderTop: "1px solid",
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4,
                            p: 3,
                            pb: 0,
                          }}
                        >
                          <Typography variant="h5" gutterBottom>{tier.name}</Typography>
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 1)}
                    </TableRow>
                    {/* Row 2: Descriptions */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`desc-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            px: 3,
                            py: 0,
                          }}
                        >
                          <Tooltip title={tier.description} arrow>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 2,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minHeight: "2.5em",
                              }}
                            >
                              {tier.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 2)}
                    </TableRow>
                    {/* Row 3: Prices */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`price-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            px: 3,
                            py: 0,
                            pb: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
                            {tier.isContactSales ? (
                              <Typography variant="h6">{tier.priceLabel}</Typography>
                            ) : (
                              <>
                                <Typography variant="h3">
                                  ${billingPeriod === "annual"
                                    ? tier.pricing?.annual ?? tier.price ?? 0
                                    : tier.pricing?.monthly ?? tier.price ?? 0}
                                </Typography>
                                <Typography variant="body1" sx={{ ml: 1 }}>
                                  per month
                                </Typography>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 3)}
                    </TableRow>
                    {/* Row 4: Configuration summary (companies/seats) */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`config-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            px: 3,
                            py: 0,
                          }}
                        >
                          {tier.isContactSales && tier.priceSubLabel ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {tier.priceSubLabel}
                            </Typography>
                          ) : (tier.baseCompanies || tier.baseSeats) ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                              {tier.baseCompanies || 0}{" "}
                              {(tier.baseCompanies || 0) === 1 ? "company" : "companies"}{" "}
                              • {tier.baseSeats || 1}{" "}
                              {(tier.baseSeats || 1) === 1 ? "seat" : "seats"}
                            </Typography>
                          ) : null}
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 4)}
                    </TableRow>
                    {/* Row 5: Credits info */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`credits-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            px: 3,
                            py: 0,
                          }}
                        >
                          {tier.isContactSales ? (
                            (tier.creditLimit || tier.baseCreditLimit) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                Custom credits/month
                              </Typography>
                            )
                          ) : (tier.creditLimit || tier.baseCreditLimit) ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {(tier.creditLimit || tier.baseCreditLimit || 0).toLocaleString()} credits/month
                            </Typography>
                          ) : null}
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 5)}
                    </TableRow>
                    {/* Row 6: Select button */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`button-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            px: 3,
                            py: 0,
                            pt: 2,
                          }}
                        >
                          {tier.isContactSales ? (
                            <Button
                              variant="outlined"
                              component="a"
                              href={`mailto:${tier.salesEmail}`}
                              fullWidth
                            >
                              Contact Sales
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handlePlanClick(tier)}
                              fullWidth
                            >
                              Get Started
                            </Button>
                          )}
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 6)}
                    </TableRow>
                    {/* Row 7: Divider */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`divider-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            px: 3,
                            pt: 3,
                            pb: 0,
                          }}
                        >
                          <Divider />
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 7)}
                    </TableRow>
                    {/* Row 8: Features */}
                    <TableRow>
                      {tierChunk.map((tier: TierData) => (
                        <TableCell
                          key={`features-${tier.id}`}
                          sx={{
                            ...cellStyle,
                            borderLeft: "1px solid",
                            borderRight: "1px solid",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            borderBottomLeftRadius: 4,
                            borderBottomRightRadius: 4,
                            p: 3,
                            pt: 3,
                          }}
                        >
                          {tier.features?.map((feature: string, index: number) => (
                            <Typography
                              key={index}
                              variant={index === 0 ? "body2" : "body1"}
                              sx={{
                                ...(index === 0
                                  ? { mb: 2, fontWeight: 500 }
                                  : { display: "flex", alignItems: "center", mb: 1 }),
                              }}
                            >
                              {index > 0 && <Check sx={{ mr: 1, color: "primary.main" }} fontSize="small" />}
                              {processFeaturePlaceholders(feature, tier)}
                            </Typography>
                          ))}
                        </TableCell>
                      ))}
                      {renderEmptyCells(tierChunk, 8)}
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            ));
          })()
        )}

        {/* Contact Sales Section - shown when enterprise tier is not available */}
        {!tiersLoading && !tiers.some((t) => t.id === "enterprise") && (
          <Box
            sx={{
              mt: 4,
              p: 3,
              textAlign: "center",
              bgcolor: alpha(instanceConfig.primaryColor, 0.05),
              borderRadius: 2,
              border: 1,
              borderColor: alpha(instanceConfig.primaryColor, 0.2),
            }}
          >
            <Typography variant="h6" gutterBottom>
              Looking for something different?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Need custom features, higher limits, or enterprise-grade support? We'd love to chat.
            </Typography>
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => {
                const salesEmail = whitelabelSettings?.salesEmail || whitelabelSettings?.ownerEmail || "sales@" + window.location.hostname;
                window.open(`mailto:${salesEmail}?subject=Custom Plan Inquiry`, "_blank");
              }}
            >
              Contact Sales
            </Button>
          </Box>
        )}

        {/* Feature Comparison Table */}
        {allFeatures.length > 0 && (
          <>
            <Typography variant="h4" sx={{ mt: 8, mb: 4, textAlign: "center" }}>
              Feature Comparison
            </Typography>

            <TableContainer
              component={Paper}
              sx={{
                maxWidth: "100%",
                overflow: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow
                    sx={{ bgcolor: alpha(instanceConfig.primaryColor, 0.1) }}
                  >
                    <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                      Feature
                    </TableCell>
                    {tiers.map((tier) => (
                      <TableCell
                        key={tier.id}
                        align="center"
                        sx={{ fontWeight: "bold", minWidth: 100 }}
                      >
                        {tier.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Plan limits section */}
                  <TableRow hover>
                    <TableCell>Credits per month</TableCell>
                    {tiers.map((tier) => (
                      <TableCell key={tier.id} align="center">
                        {tier.isContactSales
                          ? "Custom"
                          : (tier.baseCreditLimit || tier.creditLimit || 0).toLocaleString()}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Seats</TableCell>
                    {tiers.map((tier) => (
                      <TableCell key={tier.id} align="center">
                        {tier.isContactSales ? "Custom" : tier.baseSeats || 1}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Companies</TableCell>
                    {tiers.map((tier) => (
                      <TableCell key={tier.id} align="center">
                        {tier.isContactSales ? "Custom" : tier.baseCompanies || 1}
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Feature rows */}
                  {allFeatures.map((featureKey) => (
                    <TableRow key={featureKey} hover>
                      <TableCell>{getFeatureDisplayName(featureKey)}</TableCell>
                      {tiers.map((tier) => (
                        <TableCell key={tier.id} align="center">
                          <FeatureCheck
                            available={tierHasFeature(tier, featureKey)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Credit Cost Breakdown Section - only show if features are enabled */}
        {allFeatures.length > 0 && (
          <>
            <Typography variant="h4" sx={{ mt: 8, mb: 4, textAlign: "center" }}>
              Credit Cost Breakdown
            </Typography>

            <TableContainer component={Paper} sx={{ maxWidth: "100%", overflow: "auto" }}>
          <Table sx={{ minWidth: 400 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(instanceConfig.primaryColor, 0.1) }}>
                <TableCell sx={{ fontWeight: "bold" }}>Feature</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>Credits</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* AI Models Section - always show if chat or employee features are enabled */}
              {(allFeatures.includes("chat") || allFeatures.includes("employee") || allFeatures.includes("agent")) && (
                <>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontWeight: "bold" }}>
                      AI Models (per interaction)
                    </TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Budget (GPT-OSS 120B)</TableCell>
                    <TableCell align="right">0.1</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Economy (DeepSeek V3.2, GPT-4.1 Nano, GPT-5 Nano)</TableCell>
                    <TableCell align="right">0.2</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Standard (Claude Haiku 4.5, GPT-4.1 Mini, GPT-5 Mini, Gemini 2.5 Flash)</TableCell>
                    <TableCell align="right">0.5</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Premium (GPT-4.1, GPT-5, GPT-5.1, Claude Sonnet 4.5, Gemini 2.5 Pro, Grok 4)</TableCell>
                    <TableCell align="right">1.0</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Advanced (DeepSeek R1, o4-mini, Claude Opus 4.5, Perplexity Pro/Reasoning, Kimi K2)</TableCell>
                    <TableCell align="right">2.0</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Research (Perplexity Deep Research)</TableCell>
                    <TableCell align="right">3.0</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Reasoning (o3)</TableCell>
                    <TableCell align="right">4.0</TableCell>
                  </TableRow>
                </>
              )}

              {/* Content Generation Section */}
              {(allFeatures.includes("image") || allFeatures.includes("audio") || allFeatures.includes("video")) && (
                <>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontWeight: "bold" }}>
                      Content Generation
                    </TableCell>
                  </TableRow>
                  {allFeatures.includes("image") && (
                    <>
                      <TableRow hover>
                        <TableCell>Image Generation (OpenAI, Google)</TableCell>
                        <TableCell align="right">5</TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell>Image Generation (Leonardo AI)</TableCell>
                        <TableCell align="right">2</TableCell>
                      </TableRow>
                    </>
                  )}
                  {allFeatures.includes("audio") && (
                    <TableRow hover>
                      <TableCell>Audio Generation / Transcription (OpenAI, ElevenLabs)</TableCell>
                      <TableCell align="right">5</TableCell>
                    </TableRow>
                  )}
                  {allFeatures.includes("video") && (
                    <>
                      <TableRow hover>
                        <TableCell>Video Generation (Veo 3, Seedance 2)</TableCell>
                        <TableCell align="right">30</TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell>Video Additional Seconds</TableCell>
                        <TableCell align="right">+5/sec</TableCell>
                      </TableRow>
                    </>
                  )}
                </>
              )}

              {/* Browser & Automation Section */}
              {(allFeatures.includes("smart-list") || allFeatures.includes("workflow")) && (
                <>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontWeight: "bold" }}>
                      Browser & Automation
                    </TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Browser Task (standard)</TableCell>
                    <TableCell align="right">25</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Browser Task (with residential proxy)</TableCell>
                    <TableCell align="right">50</TableCell>
                  </TableRow>
                </>
              )}

              {/* Data Operations Section */}
              {(allFeatures.includes("smart-list") || allFeatures.includes("employee") || allFeatures.includes("agent") || allFeatures.includes("knowledge-base")) && (
                <>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontWeight: "bold" }}>
                      Data & Research
                    </TableCell>
                  </TableRow>
                  {allFeatures.includes("smart-list") && (
                    <TableRow hover>
                      <TableCell>Lead Generation (People Search)</TableCell>
                      <TableCell align="right">1/lead</TableCell>
                    </TableRow>
                  )}
                  {(allFeatures.includes("employee") || allFeatures.includes("agent")) && (
                    <TableRow hover>
                      <TableCell>AI Employee/Agent Research</TableCell>
                      <TableCell align="right">2/execution</TableCell>
                    </TableRow>
                  )}
                  {allFeatures.includes("knowledge-base") && (
                    <>
                      <TableRow hover>
                        <TableCell>Premium PDF Upload (Multimodal)</TableCell>
                        <TableCell align="right">5</TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell>Document Processing</TableCell>
                        <TableCell align="right">1/1K tokens</TableCell>
                      </TableRow>
                    </>
                  )}
                </>
              )}

              {/* Email Section */}
              {allFeatures.includes("inbox") && (
                <>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontWeight: "bold" }}>
                      Email & Inbox (monthly recurring)
                    </TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Platform Inbox</TableCell>
                    <TableCell align="right">20/month</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Inbox Warming</TableCell>
                    <TableCell align="right">20/month</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
          </>
        )}

        {/* FAQ Section */}
        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Typography variant="h5" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }} justifyContent="center">
            <Grid item xs={12} md={tiers.some((t) => t.id === "free") ? 4 : 6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    What are credits?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credits are used for AI operations like chat messages,
                    content generation, browser tasks, and more. Different
                    operations cost different amounts of credits based on the AI
                    model used.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={tiers.some((t) => t.id === "free") ? 4 : 6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Can I change plans?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yes! You can upgrade or downgrade your plan at any time.
                    When upgrading, you'll be charged the prorated difference.
                    When downgrading, the change takes effect at the next
                    billing cycle.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {tiers.some((t) => t.id === "free") && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Is there a free plan?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Yes! We offer a free plan that lets you explore core features
                      with limited credits. No credit card required to get started.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>

      <BrandedPageFooter currentPage="pricing" />
    </Box>
  );
};

export default Pricing;
